import { useState } from "react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/Sheet"
import { Table, TableBody, TableCell, TableHead } from "@/components/ui/Table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { type ListProduct, type Tab, SUPPLIERS, units } from "../listData"
import { DenseHead, HeaderLine, MarketLine } from "./ListProductCard"

const TABS: { tab: Tab; label: string }[] = [
  { tab: "buy", label: "To buy" },
  { tab: "no_plan", label: "No plan" },
  { tab: "no_supplier", label: "No supplier" },
]
const ALL = "all"
const NONE = "none"
const SMALL = "h-6 shrink-0 px-2 text-xs"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidates: ListProduct[]
  onAdd: (product: ListProduct) => void
}

// The add area, moved off the page into a side panel: same tabs, search and
// supplier filter as today. Stays open after an add, so the buyer can keep
// going; the list behind it shows each product landing at the top.
export default function AddProductsSheet({ open, onOpenChange, candidates, onAdd }: Props) {
  const [tab, setTab] = useState<Tab>("buy")
  const [query, setQuery] = useState("")
  const [supplier, setSupplier] = useState(ALL)

  const wanted = query.trim().toLowerCase()
  const matching = candidates
    .filter((p) => !wanted || p.name.toLowerCase().includes(wanted) || p.id.includes(wanted))
    .filter((p) => supplier === ALL || (supplier === NONE ? p.supplier === null : p.supplier === supplier))
  const shown = matching.filter((p) => p.tab === tab)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[1180px] gap-3">
        <SheetTitle>Add products</SheetTitle>
        <SheetDescription>Products not on the list yet. The ones we think need buying come first.</SheetDescription>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
            <TabsList variant="segmented">
              {TABS.map((t) => (
                <TabsTrigger key={t.tab} value={t.tab} variant="segmented">
                  {t.label} ({matching.filter((p) => p.tab === t.tab).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Input className="h-9 w-64" placeholder="Search product or SKU" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent align="start">
              <SelectItem value={ALL}>All suppliers</SelectItem>
              {SUPPLIERS.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              <SelectItem value={NONE}>No supplier on record</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-md border bg-card">
          {shown.length === 0 && <div className="px-4 py-6 text-center text-sm">Nothing here.</div>}
          {shown.map((product) => {
            const windowless = product.supplier === null
            return (
              <div key={product.id} className="border-b last:border-b-0">
                <HeaderLine product={product}>
                  <span className="shrink-0 font-medium">{product.supplier ?? "No supplier on record"}</span>
                  {!windowless && <span className="shrink-0 whitespace-nowrap">Lead time {product.leadTimeDays} days</span>}
                  {product.moq !== null && <span className="shrink-0 whitespace-nowrap">MOQ {units(product.moq)}</span>}
                  {product.tab === "buy" && <Button className={SMALL} onClick={() => onAdd(product)}>Add to list</Button>}
                  {windowless && <Button variant="outline" className={SMALL}>Add supplier</Button>}
                </HeaderLine>
                <Table className="table-fixed [&_td]:px-2 [&_td]:py-0.5">
                  <DenseHead trailing={<TableHead className="h-7 w-28 px-2 py-0 text-xs" />} />
                  <TableBody>
                    {product.lines.map((line) => (
                      <MarketLine
                        key={line.market}
                        line={line}
                        trailing={
                          <TableCell className="w-28 py-0! text-right">
                            {product.tab === "no_plan" && (
                              <Button variant="outline" className={SMALL} onClick={() => onAdd(product)}>Add to list</Button>
                            )}
                          </TableCell>
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
  )
}
